import React, { useState, useEffect, useContext } from 'react'
import { LocalizationContext } from '../../../shared/contexts'
import { formatTenantDateTime } from '../../../../utils/formatTenantDateTime'
import axios from 'axios'
import startCase from 'lodash/startCase'
import { Box, CircularProgress, Typography, Chip } from '@mui/material'

import { TIME_SOURCE, timeSourceTitle } from '../../../../utils/timeSource'

interface SmsRow {
  id: number
  message_type: string
  status: string
  message: string
  error_message: string | null
  twilio_message_sid: string | null
  queued_order_id: number | null
  phone_number: string | null
  created_at: string
}

const statusChipColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  if (['sent', 'delivered', 'read', 'accepted'].includes(status)) return 'success'
  if (['pending', 'queued'].includes(status)) return 'warning'
  if (['failed', 'undelivered'].includes(status)) return 'error'
  return 'default'
}

const SmsSection = ({ basePath }: { basePath: string }) => {
  const [rows, setRows] = useState<SmsRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const localization = useContext(LocalizationContext)
  const formatDateTime = (value: string | null) => formatTenantDateTime(value, localization)

  useEffect(() => {
    axios.get(`${basePath}/sms_notifications`)
      .then(({ data }) => {
        setRows(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [basePath])

  if (loading) {
    return <Box className='text-center py-3'><CircularProgress size={24} /></Box>
  }

  if (!rows || rows.length === 0) {
    return <Typography variant='body2' color='text.secondary'>No SMS notifications sent for this order.</Typography>
  }

  return (
    <table className='table table-robis order-show-sms-table'>
      <thead className='thead-robis'>
        <tr>
          <th>Sent at</th>
          <th>Type</th>
          <th>Status</th>
          <th>Phone</th>
          <th>Message</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td title={timeSourceTitle(row.created_at, TIME_SOURCE.app)}>{formatDateTime(row.created_at)}</td>
            <td>{startCase(row.message_type)}</td>
            <td><Chip label={row.status} color={statusChipColor(row.status)} size='small' /></td>
            <td>{row.phone_number || '—'}</td>
            <td className='sms-message-cell'>
              <div>{row.message}</div>
              {row.error_message && (
                row.status === 'skipped'
                  ? <div className='text-muted small'>{row.error_message}</div>
                  : <div className='text-danger small'>Error: {row.error_message}</div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default SmsSection
